import React, { useState } from 'react';
import { ArrowLeft, Globe, CheckCircle, Star, Monitor, Smartphone, Clock, Gift, Zap, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Packages = () => {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'ar'>('en');

  const websitePackages = [
    {
      id: 'cross-site',
      en: {
        name: 'CROSS | SITE',
        price: '120 - 150 KD',
        description: 'Professional website for small businesses',
        deliveryTime: '7-10 Days',
        features: [
          '5-7 Pages (Content Included)',
          'Mobile Responsive',
          'Contact Form',
          'Google Maps Integration',
          'Custom Design',
          'Social Media Links'
        ],
        addOn: 'MICROLINK PACKAGE FOR 15 KD',
        gifts: '2 FREE modifications within 30 days',
        onDemand: '10 KD/issue',
        popular: false
      },
      ar: {
        name: 'كروس | سايت',
        price: '١٢٠ - ١٥٠ د.ك',
        description: 'موقع إلكتروني احترافي للشركات الصغيرة',
        deliveryTime: '٧-١٠ أيام',
        features: [
          '٥-٧ صفحات (المحتوى مشمول)',
          'متجاوب مع الهواتف الذكية',
          'نموذج تواصل',
          'تكامل خرائط جوجل',
          'تصميم مخصص',
          'روابط وسائل التواصل الاجتماعي'
        ],
        addOn: 'باقة مايكرولينك بـ ١٥ د.ك',
        gifts: '٢ تعديل مجاني خلال ٣٠ يوم',
        onDemand: '١٠ د.ك/مشكلة',
        popular: false
      }
    },
    {
      id: 'cross-prime',
      en: {
        name: 'CROSS | PRIME',
        price: '250 - 350 KD',
        description: 'Premium solution for growing businesses',
        deliveryTime: '14-20 Days',
        features: [
          '10-15 Pages (Content Included)',
          'All Basic Features',
          'Advanced Design',
          'Appointment Booking',
          'WhatsApp Integration',
          'Gallery/Portfolio',
          'Advanced Custom Design',
          'SSL Certificate'
        ],
        addOn: 'MEDIALLINK PACKAGE FOR 20 KD',
        gifts: '5 FREE modifications within 30 days',
        onDemand: '20 KD/issue',
        popular: true
      },
      ar: {
        name: 'كروس | برايم',
        price: '٢٥٠ - ٣٥٠ د.ك',
        description: 'حل متميز للشركات النامية',
        deliveryTime: '١٤-٢٠ يوم',
        features: [
          '١٠-١٥ صفحة (المحتوى مشمول)',
          'جميع الميزات الأساسية',
          'تصميم متقدم',
          'حجز المواعيد',
          'تكامل واتساب',
          'معرض/ملف أعمال',
          'تصميم مخصص متقدم',
          'شهادة أمان SSL'
        ],
        addOn: 'باقة ميديالينك بـ ٢٠ د.ك',
        gifts: '٥ تعديلات مجانية خلال ٣٠ يوم',
        onDemand: '٢٠ د.ك/مشكلة',
        popular: true
      }
    }
  ];

  const toggleLanguage = () => {
    setCurrentLanguage(currentLanguage === 'en' ? 'ar' : 'en');
  };

  const isArabic = currentLanguage === 'ar';

  return (
    <div className={`min-h-screen bg-black text-white py-12 px-4 ${isArabic ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`flex items-center justify-between mb-8 ${isArabic ? 'flex-row-reverse' : ''}`}>
          <Link 
            to="/" 
            className={`flex items-center text-gray-400 hover:text-white transition-colors duration-300`}
          >
            <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
          </Link>
          
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
          >
            <Globe className="w-5 h-5 mr-2" />
            {isArabic ? 'English' : 'العربية'}
          </button>
        </div>

        {/* Page Title */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Monitor className="w-12 h-12 text-blue-500 mr-3" />
            <h1 className="text-4xl font-bold">
              {isArabic ? 'باقات المواقع الإلكترونية' : 'Website Building Packages'}
            </h1>
          </div>
          <p className="text-gray-400 text-lg">
            {isArabic ? 'اختر الباقة المناسبة لعملك' : 'Choose the perfect package for your business'}
          </p>
        </div>

        {/* Website Building Section */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {websitePackages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 hover:from-gray-800 hover:to-gray-700 transition-all duration-300 transform hover:scale-105 ${
                  pkg[currentLanguage].popular ? 'ring-2 ring-blue-500 shadow-2xl shadow-blue-500/20' : ''
                }`}
              >
                {pkg[currentLanguage].popular && (
                  <div className={`absolute -top-4 ${isArabic ? 'right-8' : 'left-8'} bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center`}>
                    <Zap className="w-4 h-4 mr-1" />
                    {isArabic ? 'الأكثر طلباً' : 'Most Demanded'}
                  </div>
                )}
                
                <div className={`${isArabic ? 'text-right' : 'text-left'}`}>
                  <h3 className="text-2xl font-bold text-blue-400 mb-2">
                    {pkg[currentLanguage].name}
                  </h3>
                  
                  <div className="flex items-center mb-4">
                    <div className="text-3xl font-bold text-white">
                      {pkg[currentLanguage].price}
                    </div>
                  </div>
                  
                  <p className="text-gray-400 mb-6">
                    {pkg[currentLanguage].description}
                  </p>

                  {/* Delivery Time */}
                  <div className={`flex items-center mb-4 text-green-400 ${isArabic ? 'flex-row-reverse' : ''}`}>
                    <Clock className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                    <span className="font-medium">{pkg[currentLanguage].deliveryTime}</span>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {pkg[currentLanguage].features.map((feature, index) => (
                      <li key={index} className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <CheckCircle className={`w-5 h-5 text-blue-500 flex-shrink-0 ${isArabic ? 'ml-3' : 'mr-3'}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Special Offers */}
                  <div className="space-y-3 mb-6">
                    {/* Add-on */}
                    <div className={`bg-green-900/30 border border-green-500/30 rounded-lg p-3 ${isArabic ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center text-green-400 mb-1 ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <Star className={`w-4 h-4 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                        <span className="font-medium text-sm">
                          {isArabic ? 'إضافة بخصم' : 'ADD ON WITH DISCOUNT'}
                        </span>
                      </div>
                      <p className="text-sm text-green-300">{pkg[currentLanguage].addOn}</p>
                    </div>

                    {/* Gifts */}
                    <div className={`bg-purple-900/30 border border-purple-500/30 rounded-lg p-3 ${isArabic ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center text-purple-400 mb-1 ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <Gift className={`w-4 h-4 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                        <span className="font-medium text-sm">
                          {isArabic ? 'هدايا' : 'GIFTS'}
                        </span>
                      </div>
                      <p className="text-sm text-purple-300">{pkg[currentLanguage].gifts}</p>
                    </div>

                    {/* On Demand */}
                    <div className={`bg-orange-900/30 border border-orange-500/30 rounded-lg p-3 ${isArabic ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center text-orange-400 mb-1 ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <Smartphone className={`w-4 h-4 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                        <span className="font-medium text-sm">
                          {isArabic ? 'عند الطلب' : 'ON DEMAND'}
                        </span>
                      </div>
                      <p className="text-sm text-orange-300">{pkg[currentLanguage].onDemand}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center bg-gradient-to-r from-green-900 to-blue-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4">
            {isArabic ? 'جاهز لبدء مشروعك؟' : 'Ready to Start Your Project?'}
          </h2>
          <p className="text-gray-300 mb-6">
            {isArabic ? 'تواصل معنا الآن للحصول على استشارة مجانية وعرض سعر مخصص' : 'Contact us now for a free consultation and custom quote'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/96598551104"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-medium transition-colors duration-300 flex items-center justify-center text-lg"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              {isArabic ? 'واتساب الآن' : 'WhatsApp Now'}
            </a>
            <a
              href="https://forms.gle/opDVc3ZdYm9tpTUg6"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-medium transition-colors duration-300 text-lg"
            >
              {isArabic ? 'احجز استشارة مجانية' : 'Book Free Consultation'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;